import {
  Box,
  ContentTemplate,
  HeaderInput,
  MUIDataTable,
  MUIDataTableColumnDef,
  SectionHeader,
} from 'web-library';
import React from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  AccessConfigDetails,
  UserGroupAccessOption,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import UserGroupAccessOptionsDataLevel from 'web-library/components/UserGroupAccessOptionsDataLevel';

interface TableColumnsProps {
  dropdownList: UserGroupAccessOption[][];
  t: TFunction;
  functionListArrayWithSorting: string[][];
  defaultSelectedDataAccessLevel: string[][];
}

const tableColumns = ({
  dropdownList,
  t,
  functionListArrayWithSorting,
  defaultSelectedDataAccessLevel,
}: TableColumnsProps): MUIDataTableColumnDef[] => [
  {
    name: 'displayName',
    label: t('admin->>UserGroups.Function'),
    options: {
      sort: false,
      filter: false,
      setCellProps: () => ({ style: { width: '50%' } }),
      customBodyRenderLite: (dataIndex: number) => <>{functionListArrayWithSorting[dataIndex]}</>,
    },
  },
  {
    name: 'dataAccessLevel',
    label: t('admin->>UserGroups.DataAccessLevel'),
    options: {
      sort: false,
      filter: false,
      setCellProps: () => ({ style: { width: '50%' } }),
      customBodyRenderLite: (dataIndex: number) => (
        <UserGroupAccessOptionsDataLevel
          accessOptions={dropdownList[dataIndex]}
          selectedAccessOptions={defaultSelectedDataAccessLevel[dataIndex]}
          valueTitle={`content[${dataIndex}].selectedAccessOptions`}
          placeholder={t('admin->>UserGroups.SelectDataAccessLevelPlaceholder')}
          onChangeHandler={() => null}
          editable={false}
        />
      ),
    },
  },
];

interface FunctionAccessDetailsProps {
  accessConfigurationListingData: AccessConfigDetails;
  selectedInstitution: { key: string; value: string };
  targetRole: string;
}

const FunctionAccessDetails = ({
  accessConfigurationListingData,
  selectedInstitution,
  targetRole,
}: FunctionAccessDetailsProps) => {
  const { t } = useTranslation();

  const functionListArray: string[][] = [];

  Object.keys(accessConfigurationListingData.authorities).forEach((obj) =>
    functionListArray.push([obj])
  );

  const functionListArrayWithSorting = [...functionListArray].sort();// NOSONAR

  const authoritiesEntry = [];

  Object.entries(accessConfigurationListingData.authorities).forEach((obj) =>
    authoritiesEntry.push([obj])
  );

  const dropdownUuidDisplayList: string[][][] = [];

  Object.entries(accessConfigurationListingData.authorities)
    .sort()// NOSONAR
    .forEach((authoritiesDetailsValue) => {
      dropdownUuidDisplayList.push(Object.entries(authoritiesDetailsValue[1].accessOptions));
    });

  const dropdownList: UserGroupAccessOption[][] = [];

  dropdownUuidDisplayList.forEach((dropdown) => {
    const featureDropdownList: UserGroupAccessOption[] = [];
    dropdown.forEach((a) => {
      featureDropdownList.push({
        uuid: a[1],
        displayName: a[0],
      });
    });
    dropdownList.push(featureDropdownList);
  });

  const result: string[][] = [];
  const { accessMatrix } = accessConfigurationListingData.accessMatrixConfigurations[
    selectedInstitution.key
  ];

  Object.keys(accessConfigurationListingData.authorities)
    .sort() // NOSONAR
    .forEach((authority) => {
      if (!accessMatrix[authority] || accessMatrix[authority].length <= 0) {
        result.push([]);
      } else {
        result.push(
          accessMatrix[authority].map(
            (accessLevel) =>
              accessConfigurationListingData.authorities[authority]?.accessOptions[accessLevel]
          )
        );
      }
    });
  return (
    <ContentTemplate type="full">
      <SectionHeader title={t('admin->>UserGroups.FunctionAccessDetails')} />
      <HeaderInput
        label={t('admin->>UserGroups.TargetRole')}
        input={<Box>{targetRole}</Box>}
        topBottom={false}
      />
      <HeaderInput
        label={t('admin->>UserGroups.Institution')}
        input={<Box>{selectedInstitution.key}</Box>}
        topBottom={false}
      />
      {accessConfigurationListingData && (
        <MUIDataTable
          key={selectedInstitution.key}
          columns={tableColumns({
            dropdownList,
            t,
            functionListArrayWithSorting,
            defaultSelectedDataAccessLevel: result,
          })}
          data={functionListArrayWithSorting}
        />
      )}
    </ContentTemplate>
  );
};

export default FunctionAccessDetails;
